import { useCallback, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';

import { SectionCard } from '../components/ttq/SectionCard';
import {
  factoryService,
  type FactoryBatch,
} from '../services/factoryService';

export function OwnerFactoryAuthPage() {
  const [batches, setBatches] = useState<FactoryBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const list = await factoryService.listOwnerPendingBatches();
    setBatches(list);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  async function authorize(
    batchId: string,
    decision: 'authorize_factory' | 'authorize_self' | 'reject',
  ) {
    setBusyId(batchId);
    setError(null);
    try {
      await factoryService.authorizeOwnerBatch(batchId, decision);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <div>
        <h1 className="text-xl font-semibold text-ink">工厂加工授权</h1>
        <p className="mt-1 text-sm text-muted">
          素材码绑定的日记账户为 Owner。批准工厂处理、自己处理，或拒绝。
        </p>
      </div>

      {error ? (
        <p className="rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <SectionCard title="待授权批次">
        {loading ? (
          <p className="text-sm text-muted">加载中…</p>
        ) : batches.length === 0 ? (
          <p className="text-sm text-muted">暂无待授权批次</p>
        ) : (
          <ul className="space-y-3">
            {batches.map((b) => (
              <li
                key={b.id}
                className="rounded-xl border border-border bg-canvas/40 px-4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{b.title || '未命名批次'}</div>
                    <div className="mt-1 text-xs text-muted">
                      状态：{b.processingAuthorization || 'awaiting_owner_auth'} ·{' '}
                      {new Date(b.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busyId === b.id}
                      onClick={() => void authorize(b.id, 'authorize_factory')}
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs text-white disabled:opacity-50"
                    >
                      <Check size={14} />
                      授权工厂
                    </button>
                    <button
                      type="button"
                      disabled={busyId === b.id}
                      onClick={() => void authorize(b.id, 'authorize_self')}
                      className="rounded-lg border border-border px-3 py-1.5 text-xs text-ink disabled:opacity-50"
                    >
                      自己处理
                    </button>
                    <button
                      type="button"
                      disabled={busyId === b.id}
                      onClick={() => void authorize(b.id, 'reject')}
                      className="inline-flex items-center gap-1 rounded-lg border border-danger/40 px-3 py-1.5 text-xs text-danger disabled:opacity-50"
                    >
                      <X size={14} />
                      拒绝
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
